import { gql } from 'graphql-request';
import {
  FetchAccountApi,
  ChatHistoryResponse,
  ChatMessagesResponse,
  SessionHistoryResponse,
  SessionStatus,
  UserSessionUsage,
} from '../types';
import { getBrowserTimezone } from '../utils/datetime';
import { patch, post, get } from '../utils/http';

type ApiContext = {
  environment_ids?: string[];
  incident_ids?: string[];
  asset_ids?: string[];
  test_ids?: string[];
};

export const setIsCriticalAssets = ({
  ids,
  isCritical,
  envId,
}: {
  ids: string[];
  isCritical: boolean;
  envId: string;
}) =>
  patch(`${window.VITE_API_BASE_URL}/${envId}/assets/`, {
    asset_ids: ids,
    is_critical: isCritical,
  });

export const sendTrackingEvent = (
  data: { eventName: string; properties: any },
  envId: string,
) => post(`${window.VITE_API_BASE_URL}/${envId}/events`, data);

export const reportViewMetric = (
  data: { view_name: string; duration: number },
  envId: string,
) => post(`${window.VITE_API_BASE_URL}/${envId}/view-metrics`, data);

export const fetchAccount = (): Promise<FetchAccountApi> =>
  get(`${window.VITE_API_BASE_URL}/account/`);

export const fetchAiQuota = (): Promise<UserSessionUsage | null> => {
  const tz = getBrowserTimezone();
  return get(`${window.VITE_API_BASE_URL}/ai-chat/quota`, undefined, {
    headers: tz ? { 'X-User-Timezone': tz } : undefined,
  });
};

export const fetchChatHistory = (
  sinceDate: string,
  limit: number = 50,
  offset: number = 0,
): Promise<ChatHistoryResponse> =>
  get(`${window.VITE_API_BASE_URL}/ai-chat/history`, {
    since_date: sinceDate,
    limit,
    offset,
  });

export const fetchChatMessages = (
  threadId: string,
): Promise<ChatMessagesResponse> =>
  get(`${window.VITE_API_BASE_URL}/ai-chat/messages`, {
    thread_id: threadId,
  });

const userTimezoneHeaders = (): Record<string, string> | undefined => {
  const tz = getBrowserTimezone();
  return tz ? { 'X-User-Timezone': tz } : undefined;
};

export const createSession = (body: {
  prompt: string;
  mode?: string;
  model?: string;
  context?: ApiContext;
  environment_id?: string;
}): Promise<{ session_id: string }> =>
  post(`${window.VITE_API_BASE_URL}/ai/sessions`, body, {
    headers: userTimezoneHeaders(),
  });

export const sendSessionMessage = (
  sessionId: string,
  body: { prompt: string; mode?: string; model?: string; context?: ApiContext },
): Promise<void> =>
  post(`${window.VITE_API_BASE_URL}/ai/sessions/${sessionId}/messages`, body, {
    headers: userTimezoneHeaders(),
  });

export const respondToSessionTool = (
  sessionId: string,
  body: {
    kind: 'approval' | 'output';
    tool_call_id: string;
    approved?: boolean;
    args_override?: Record<string, unknown>;
    value?: unknown;
  },
): Promise<void> =>
  post(
    `${window.VITE_API_BASE_URL}/ai/sessions/${sessionId}/tool-response`,
    body,
  );

export const cancelSession = (sessionId: string): Promise<void> =>
  post(`${window.VITE_API_BASE_URL}/ai/sessions/${sessionId}/cancel`);

export const fetchSessionHistory = (
  limit: number = 50,
  offset: number = 0,
): Promise<SessionHistoryResponse> =>
  get(`${window.VITE_API_BASE_URL}/ai/sessions`, { limit, offset });

export const buildSessionEventsUrl = (
  sessionId: string,
  fromSeq: number = -1,
): string =>
  `${window.VITE_API_BASE_URL}/ai/sessions/${sessionId}/events?from_seq=${fromSeq}`;

export const fetchSessionEvents = (
  sessionId: string,
  fromSeq: number = -1,
): Promise<{ status: SessionStatus | null; events: Record<string, any>[] }> =>
  get(`${window.VITE_API_BASE_URL}/ai/sessions/${sessionId}/events/history`, {
    from_seq: fromSeq,
  });

export const getTreeViewQuery = gql`
  query GetTreeView(
    $category: TreeCategory!
    $view: TreeViewType!
    $filters: [Filter!]
    $includeTotals: Boolean!
  ) {
    treeView(
      category: $category
      view: $view
      filters: $filters
      includeTotals: $includeTotals
      includeInternalAssets: false
    ) {
      name
      data
    }
  }
`;

export const getTreeStatusTotalsQuery = gql`
  query GetTreeStatusTotals(
    $category: TreeCategory!
    $view: TreeViewType!
    $daysBack: Int!
    $items: [TreeStatusTotalsItemInput!]!
    $filters: [Filter!]
    $excludeResourceTypes: [String!]
  ) {
    treeStatusTotals(
      category: $category
      view: $view
      daysBack: $daysBack
      items: $items
      filters: $filters
      excludeResourceTypes: $excludeResourceTypes
      includeInternalAssets: false
    ) {
      requestId
      totals {
        passed
        warnings
        failures
        errors
      }
    }
  }
`;

export const getTreeIncidentStatusTotalsQuery = gql`
  query GetTreeIncidentStatusTotals(
    $category: TreeCategory!
    $view: TreeViewType!
    $daysBack: Int!
    $items: [TreeStatusTotalsItemInput!]!
    $filters: [Filter!]
    $excludeResourceTypes: [String!]
  ) {
    treeIncidentStatusTotals(
      category: $category
      view: $view
      daysBack: $daysBack
      items: $items
      filters: $filters
      excludeResourceTypes: $excludeResourceTypes
      includeInternalAssets: false
    ) {
      requestId
      statusCounts {
        open
        acknowledged
        resolved
      }
    }
  }
`;
